<script setup>
import { computed, nextTick, ref } from "vue";
import { useStore } from "vuex";

// One question with its answers laid out as chips: tap to pick as many as fit, edit a
// picked answer with the pencil, or add your own. Main questions can be swapped for a
// different one.
const props = defineProps({
  question: { type: Object, required: true },
  // Shown in the badge until the question is answered
  number: { type: Number, default: 0 },
  swappable: { type: Boolean, default: false },
});

const store = useStore();
const chosen = computed(
  () => store.getters["questions/chosenOptionsById"][props.question.id] || []
);
const answered = computed(() => chosen.value.length > 0);
const isChosen = (answer) => chosen.value.includes(answer);

const toggle = (option) =>
  store.dispatch("questions/toggleSelect", {
    option,
    question: props.question,
  });
const swap = () =>
  store.dispatch("questions/replaceQuestion", {
    questionId: props.question.id,
  });

// Answer boxes grow with what's typed, like the chips they replace
const fitWidth = (text, least) =>
  `${Math.min(Math.max(text.length + 4, least), 60)}ch`;

// Adding your own answer
const adding = ref(false);
const ownAnswer = ref("");
const ownInput = ref(null);
const startAdding = async () => {
  adding.value = true;
  await nextTick();
  ownInput.value?.focus();
};
const addOwn = () => {
  const value = ownAnswer.value.trim();
  if (value) {
    if (props.question.suggestedAnswers.includes(value)) {
      if (!isChosen(value)) toggle(value);
    } else {
      store.dispatch("questions/addAnswer", {
        value,
        question: props.question,
      });
    }
  }
  ownAnswer.value = "";
  adding.value = false;
};
const cancelAdding = () => {
  ownAnswer.value = "";
  adding.value = false;
};

// Editing a picked answer
const editing = ref("");
const editValue = ref("");
const editInput = ref(null);
const startEdit = async (answer) => {
  editing.value = answer;
  editValue.value = answer;
  await nextTick();
  editInput.value?.[0]?.focus();
};
const saveEdit = () => {
  if (!editing.value) return;
  const value = editValue.value.trim();
  const clash =
    value !== editing.value && props.question.suggestedAnswers.includes(value);
  if (value && value !== editing.value && !clash) {
    store.commit("questions/SET_ACTIVE_EDIT", editing.value);
    store.dispatch("questions/changeAnswer", {
      value,
      question: props.question,
    });
  }
  editing.value = "";
};
const cancelEdit = () => {
  editing.value = "";
};
</script>

<template>
  <article
    class="rounded-2xl border bg-white p-3 transition-colors sm:p-5"
    :class="answered ? 'border-accentSecondary' : 'border-gray-200'"
  >
    <div class="flex items-start gap-3">
      <span
        class="mt-0.5 flex h-6 w-6 shrink-0 sm:h-7 sm:w-7 items-center justify-center rounded-full text-sm font-bold transition-colors"
        :class="
          answered
            ? 'bg-accentSecondary text-primary'
            : 'bg-gray-10 text-secondary'
        "
        aria-hidden="true"
      >
        <font-awesome-icon v-if="answered" :icon="['fas', 'check']" />
        <font-awesome-icon v-else-if="!number" :icon="['fas', 'paperclip']" />
        <template v-else>{{ number }}</template>
      </span>
      <h3 class="flex-1 font-bold text-primary lg:text-lg">
        {{ question.question }}
        <span
          v-if="question.fromContext"
          class="ml-1 whitespace-nowrap rounded-full bg-accentSecondaryLight px-2 py-0.5 align-middle text-xs font-medium"
          >Answered from your context</span
        >
      </h3>
      <button
        v-if="swappable"
        type="button"
        class="-m-2 shrink-0 rounded-full p-2 text-secondary transition-colors hover:bg-gray-10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
        title="Ask something else"
        :aria-label="`Ask something else instead of: ${question.question}`"
        @click="swap"
      >
        <font-awesome-icon :icon="['fas', 'rotate']" />
      </button>
    </div>

    <ul
      class="mt-2.5 flex flex-wrap gap-1.5 sm:mt-3 sm:gap-2 sm:pl-10"
      aria-label="Answers"
    >
      <li
        v-for="answer in question.suggestedAnswers"
        :key="answer"
        class="max-w-full"
      >
        <form
          v-if="editing === answer"
          class="flex max-w-full"
          @submit.prevent="saveEdit"
        >
          <label :for="`edit-${question.id}`" class="sr-only"
            >Change this answer</label
          >
          <input
            :id="`edit-${question.id}`"
            ref="editInput"
            v-model="editValue"
            :style="{ width: fitWidth(editValue, 8) }"
            class="max-w-full rounded-2xl border border-accentPrimary px-3 py-1.5 text-sm text-primary outline-none"
            @keydown.esc.prevent="cancelEdit"
            @blur="saveEdit"
          />
        </form>
        <span
          v-else
          class="inline-flex max-w-full items-stretch rounded-2xl border text-sm text-primary transition-colors"
          :class="
            isChosen(answer)
              ? 'border-accentSecondary bg-accentSecondary'
              : 'border-gray-200 bg-white hover:border-primary'
          "
        >
          <button
            type="button"
            class="flex items-start rounded-2xl px-2.5 py-1 text-left sm:px-3 sm:py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
            :aria-pressed="isChosen(answer)"
            @click="toggle(answer)"
          >
            <font-awesome-icon
              v-if="isChosen(answer)"
              :icon="['fas', 'check']"
              class="mr-1.5 mt-[0.2rem] shrink-0 text-xs"
            />
            <span>{{ answer }}</span>
          </button>
          <button
            v-if="isChosen(answer)"
            type="button"
            class="-ml-1 flex items-center rounded-r-2xl pl-1 pr-3 text-primary/70 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
            title="Change this answer"
            :aria-label="`Change the answer: ${answer}`"
            @click="startEdit(answer)"
          >
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-xs" />
          </button>
        </span>
      </li>
      <li class="max-w-full">
        <form
          v-if="adding"
          class="flex max-w-full gap-2"
          @submit.prevent="addOwn"
        >
          <label :for="`own-${question.id}`" class="sr-only"
            >Your own answer</label
          >
          <input
            :id="`own-${question.id}`"
            ref="ownInput"
            v-model="ownAnswer"
            placeholder="Type your answer"
            :style="{ width: fitWidth(ownAnswer, 16) }"
            class="max-w-full rounded-2xl border border-accentPrimary px-3 py-1.5 text-sm text-primary outline-none"
            @keydown.esc.prevent="cancelAdding"
            @blur="addOwn"
          />
        </form>
        <button
          v-else
          type="button"
          class="rounded-2xl border border-dashed border-secondaryLight px-2.5 py-1 text-sm sm:px-3 sm:py-1.5 text-secondary transition-colors hover:border-accentPrimary hover:text-accentPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
          @click="startAdding"
        >
          + Your own
        </button>
      </li>
    </ul>
  </article>
</template>
